<template>
    <section class="o-page-bottom-spacing--large" :class="{'u-area-disabled': pageDisabled}">
        <page-header>
            <slot slot="title">Pre-order {{ PreOrder.Brand }} - {{ PreOrder.Collection }}</slot>
            <slot slot="description">
                <p>{{ $t('preorder_edit.customer_description') }}</p>
            </slot>
        </page-header>
        <div class="o-wrapper o-wrapper--center">
            <h2 class="c-title--border u-h3">{{ $t('preorder_edit.order_details') }}</h2>
            <v-loading :data="PreOrder" custom-class="u-pos-relative"></v-loading>

            <v-message v-if="PreOrder.DeadlineExpired" custom-class="o-page-row" type="error" icon="attention"
                       icon-width="32px" icon-height="35">
                {{ $t('preorder.deadline_expired') }}
            </v-message>
            <v-message v-if="PreOrder.IsClosed" custom-class="o-page-row" type="info" icon="attention" icon-width="32px"
                       icon-height="35">
                {{ $t('preorder.status_closed') }}
            </v-message>

          <section :class="{'u-disabled': !PreOrder.Id, 'u-font-small o-gutter o-grid o-grid--third o-page-row': true}">
                <v-blocks>
                    <v-block color="neutral">
                        <table class="u-fullwidth table__order-details">
                            <tr>
                                <td class="u-bold">{{ $t('preorder_fiche.brand') }}</td>
                                <td>{{ PreOrder.Brand }}</td>
                            </tr>
                            <tr>
                                <td class="u-bold">{{ $t('preorder_fiche.season') }}</td>
                                <td>{{ PreOrder.Collection }}</td>
                            </tr>
                            <tr v-if="PreOrder.LastUpdate">
                                <td class="u-bold">{{ $t('preorder.last_update') }}</td>
                                <td>{{ PreOrder.LastUpdate | moment("DD-MM-YYYY") }}</td>
                            </tr>
                            <tr>
                                <td class="u-bold">{{ $t('preorder.state') }}</td>
                                <td>{{ PreOrder.CustomerState }}</td>
                            </tr>
                            <tr v-if="PreOrder.Deadline" :class="{'u-color-error-dark u-font-weight-bold': PreOrder.DeadlineExpired}">
                                <td class="u-bold">{{ $t('preorder.deadline') }}</td>
                                <td>{{ PreOrder.Deadline | moment("DD-MM-YYYY") }}</td>
                            </tr>
                        </table>
                    </v-block>
                </v-blocks>

                <v-blocks>
                    <v-block color="neutral">
                        <table class="u-fullwidth u-margin-none table__order-details">
                            <tr>
                                <td class="u-bold">{{ $t('preorder.customer_key') }}</td>
                                <td>{{ PreOrder.CustomerAltKey }}</td>
                            </tr>
                            <tr>
                                <td class="u-bold">{{ $t('preorder.customer_name') }}</td>
                                <td>{{ PreOrder.Customer }}</td>
                            </tr>
                            <tr>
                                <td class="u-bold">{{ $t('preorder.internal_ref_customer') }}</td>
                                <td>
                                    <v-editable @change="savePreOrderData" :max=20 :content.sync="PreOrder.InternalCustomerReference"></v-editable>
                                </td>
                            </tr>
                            <tr>
                                <td class="u-bold">{{ $t('preorder.order_ref') }}</td>
                                <td>{{ PreOrder.OrderReference }}</td>
                            </tr>
                            <tr v-if="PreOrder.PreOrderDiscount">
                                <td class="u-bold">{{ $t('preorder.discount') }}</td>
                                <td>{{ PreOrder.PreOrderDiscount }}%</td>
                            </tr>
                            <tr>
                                <td class="u-bold">{{ $t('preorder.early_shipment_allowed') }}</td>
                                <td>
                                    <v-switch v-model="PreOrder.EarlyShipmentAllowed"
                                              :name="`early_shipment_allowed_${_uid.toString()}`"></v-switch>
                                </td>
                            </tr>
                            <tr>
                                <td class="row__delivery-address" colspan="2">
                                    <div class="u-bold u-margin-bottom-tiny">{{ $t('preorder.delivery_address') }}</div>
                                    <v-address-select v-if="PreOrder.CustomerKey"
                                                      v-model="PreOrder.Address"
                                                      :customer-key="parseInt(PreOrder.CustomerKey)"
                                                      :add-address="false">
                                    </v-address-select>
                                </td>
                            </tr>
                        </table>
                    </v-block>
                    <v-block v-if="PreOrder.EarlyShipmentAllowed" color="green">
                        <v-checklist>
                            <v-checklist-item v-if="PreOrder.EarlyShipmentAllowed">{{
                                $t('preorder.early_shipment_allowed') }}
                            </v-checklist-item>
                        </v-checklist>
                    </v-block>
                </v-blocks>

                <v-blocks spacing="none">
                    <v-block color="neutral">
                        <table class="u-fullwidth u-margin-none">
                            <tr>
                                <td class="u-bold" style="width:73%">{{ $t('preorder.product_count') }}</td>
                                <td>{{ PreOrderTotals.ProductCount }}</td>
                            </tr>
                            <tr>
                                <td class="u-bold" style="width:73%">{{ $t('preorder.product_variant_count') }}</td>
                                <td>{{ PreOrderTotals.ProductVariantAmount }}</td>
                            </tr>
                            <tr>
                                <td class="u-bold" style="width:73%">{{ $t('preorder.total_price') }}</td>
                                <td>{{ PreOrderTotals.TotalProductPrice | currency(customerCurrencySymbol) }}</td>
                            </tr>
                        </table>
                    </v-block>
                    <v-block color="base">
                        <table class="u-fullwidth u-margin-none">
                            <tr>
                                <td class="u-bold u-font-default" style="width:73%">{{
                                    $t('preorder.total_price_after_discount') }}
                                </td>
                                <td>{{ PreOrderTotals.TotalProductPriceAfterDiscount | currency(customerCurrencySymbol) }}</td>
                            </tr>
                        </table>
                    </v-block>
                    <v-block size="small" color="neutral">
                        <div class="o-grid o-grid--third o-gutter-small">
                            <v-sub-group-totals></v-sub-group-totals>
                            <v-targets-and-totals v-if="PreOrder.ForecastsAndHistoryVisible"></v-targets-and-totals>
                            <v-delivery-moment-totals></v-delivery-moment-totals>
                        </div>
                    </v-block>
                    <v-block size="small">
                        <div class="c-button-group">
                            <button class="c-button c-button--fullwidth c-button--alpha"
                                    :disabled="ctaButtonDisabled || isReadOnly"
                                    @click.prevent="sendPreOrder">{{ $t('preorder.send_to_salesman') }}
                            </button>
                            <a @click.prevent="notifySaved" href="#"
                               class="c-button c-button--fullwidth c-button--outline">{{ $t('preorder.save_without_sending') }}</a>
                        </div>
                    </v-block>
                </v-blocks>
            </section>

            <h2 class="c-title--border u-h3">{{ $t('preorder.products') }}</h2>
            <v-loading :data="loaded" custom-class="u-pos-relative"></v-loading>
        </div>

        <v-filters :custom-class="loadingProducts ? 'u-disabled o-page-row' : 'o-page-row'">
            <v-filter-input :term.sync="searchTerm"
                            name="search-products"
                            :disabled="loadingProducts"
                            :placeholder="$t('edit_preorder.filter_placeholder')"
                            @keyup.enter.prevent="searchPreOrderProducts"
                            label="search"
            ></v-filter-input>
            <input name="amountsOnly" id="amountsOnly" type="checkbox" v-model="amountsOnly"/><label for="amountsOnly">{{ $t('edit_preorder.amountsOnly') }}</label>
            <slot slot="search-button">
              <button @click.prevent="searchPreOrderProducts" :disabled="loadingProducts" class="c-button c-button--alpha c-button--small" title="Zoeken">
                <svgicon name="search"></svgicon>
                {{ $t('global.search') }}
              </button>

              <button v-if="filteredProducts.length > 0"
                      type="button"
                      class="[ c-button c-button--small c-button--white ] u-flex--push-left"
                      @click="collapseAllVariations = !collapseAllVariations">
                {{collapseAllVariations ? $t('preorder_fiche.toggle_variations.expand_all') : $t('preorder_fiche.toggle_variations.collapse_all')}}
              </button>
            </slot>
        </v-filters>

        <section :class="{ 'o-wrapper o-wrapper--center o-page-row--huge u-pos-relative': true, 'u-disabled': !filteredProducts.length || loadingProducts }">

            <v-pagination class="c-pager o-page-row"
                          :config="PaginationConfig"
                          :pagination="PaginationData"
                          @paginated="fetchPaginated"
            ></v-pagination>

            <v-product-line v-for="(group, index) in filteredProducts"
                            :parent-id="PreOrder.Id"
                            :group-index="index"
                            :key="group.Id"
                            :group="group"
                            :is-editable-field="false"
                            :collapseAllVariations="collapseAllVariations"
                            :sizeChartCopy="sizeChartCopy"
                            @sizeChartCopied="sizeChartCopied">
            </v-product-line>
            <v-empty-state v-if="!PreOrder.ProductGroups">
                <slot slot="title">{{ $t('edit_preorder.no_products') }}</slot>
            </v-empty-state>
            <v-empty-state v-if="PreOrder.ProductGroups && !filteredProducts.length">
                <slot slot="title">{{ $t('edit_preorder.no_results') }}</slot>
            </v-empty-state>

            <v-pagination class="c-pager o-page-row"
                          :config="PaginationConfig"
                          :pagination="PaginationData"
                          @paginated="fetchPaginated"
            ></v-pagination>
        </section>

        <section class="o-wrapper o-wrapper--center o-page-row--medium is-marketing-line">
            <h2 class="c-title--border u-h3">{{ $t('preorder.marketing_products') }}</h2>
            <v-marketing-line v-for="(group, index) in PreOrderMarketingProducts.MarketingProductGroups"
                              :parent-id="PreOrder.Id"
                              :group-index="index"
                              :key="group.Id"
                              :group="group"
                              :is-editable-field="false">
            </v-marketing-line>
            <v-empty-state v-if="!PreOrderMarketingProducts.MarketingProductGroups || !PreOrderMarketingProducts.MarketingProductGroups.length">
                <slot slot="title">{{ $t('edit_preorder.no_marketingproducts') }}</slot>
            </v-empty-state>
        </section>

        <section class="o-wrapper o-wrapper--center o-page-row--medium">
            <v-form-row>
                <v-textarea v-model="PreOrder.CustomerComment" :horizontal="false" name="opmerkingen_klanten" cols="20"
                            rows="6"
                            value="" :fluid="true" custom-label-class="u-margin-bottom-small">
                    <slot slot="label">
                        <h3 class="c-title--border">{{ $t('edit_preorder.remarks') }}</h3>
                    </slot>
                </v-textarea>
            </v-form-row>
        </section>

        <v-chatbox v-if="!isImpersonated"
                   :receiver="PreOrder.SalesMan"
                   :salesmanPhoto="PreOrder.SalesManPhoto"
                   :unreadMessages="PreOrder.UnreadMessages"
                   @update-unread="updateUnread">

        </v-chatbox>
        <v-pre-order-concurrency-manager
                @concurrencyException="onConcurrencyException"></v-pre-order-concurrency-manager>
    </section>
</template>

<script>
    import debounce from 'lodash/debounce'
    import PageHeader from '@/components/common/PageHeader'
    import VLoading from '@/components/Loading'
    import VMessage from '@/components/Message'
    import VBlocks from '@/components/blocks/Blocks'
    import VChecklist from '@/components/checklist/Checklist'
    import VAttention from '@/components/attention/Attention'
    import VProductLine from '@/components/product/common/ProductLine'
    import VMarketingLine from '@/components/product/common/MarketingLine'
    import VEmptyState from '@/components/EmptyState'
    import VModal from '@/components/modal/Modal'
    import VSubGroupTotals from '@/components/modal/common/SubGroupTotals'
    import VDeliveryMomentTotals from '@/components/modal/common/DeliveryMomentTotals'
    import VTargetsAndTotals from '@/components/modal/common/TargetsAndTotals'
    import VChatbox from '@/components/chat/Chatbox'
    import VPreOrderConcurrencyManager from '@/components/concurrency/PreOrderConcurrencyManager'
    import VAddressSelect from '@/components/form/addressSelect/AddressSelect'
    import VPagination from '@/components/Pagination'

    // Forms
    import VTextarea from '@/components/form/Textarea'
    import VFilters from '@/components/form/filters/Filters'
    import VFormRow from '@/components/form/Row'
    import VSelect from '@/components/form/Select'
    import VCustomSelect from '@/components/form/CustomSelect'
    import VEditable from '@/components/form/Editable'
    import VSwitch from '@/components/form/Switch'

    // Store
    import {mapGetters} from 'vuex'

    // Icon
    import targetIcon from '@/assets/images/svg/targets.svg'
    import differenceIcon from '@/assets/images/svg/difference.svg'


    export default {
        components: {
            PageHeader,
            VSelect,
            VLoading,
            VMessage,
            VBlocks,
            VChecklist,
            VAttention,
            VAddressSelect,
            VTextarea,
            VFormRow,
            VFilters,
            VProductLine,
            VMarketingLine,
            VEmptyState,
            VModal,
            VSubGroupTotals,
            VDeliveryMomentTotals,
            VTargetsAndTotals,
            VCustomSelect,
            VEditable,
            VSwitch,
            targetIcon,
            differenceIcon,
            VChatbox,
            VPreOrderConcurrencyManager,
            VPagination
        },
        data() {
            return {
                disableWatch: true,
                loaded: {},
                loadingProducts: false,
                collapseAllVariations: false,
                ctaButtonDisabled: false,
                Page: 1,
                searchTerm: '',
                amountsOnly: false,
                pageDisabled: false,
                sizeChartCopy: null
            }
        },
        computed: {
            ...mapGetters({
                PreOrder: 'PreOrder',
                PreOrderTotals: 'PreOrderTotals',
                PreOrderProducts: 'PreOrderProducts',
                PreOrderMarketingProducts: 'PreOrderMarketingProducts',
                CustomerAddressOptions: 'CustomerAddressOptions'
            }),
            filteredProducts() {
                return this.PreOrderProducts.ProductGroups ? this.PreOrderProducts.ProductGroups : [];
            },
            isReadOnly() {
                return this.PreOrder.DeadlineExpired || this.PreOrder.IsClosed
            },
            isImpersonated() {
                return this.$store.getters.isImpersonated;
            },
            PaginationData() {
              return {
                PageStartIndex: this.PreOrderProducts.PageStartIndex,
                PageEndIndex: this.PreOrderProducts.PageEndIndex,
                PageLastIndex: this.PreOrderProducts.PageCount,
                TotalCount: this.PreOrderProducts.TotalCount,
                Pages: this.PreOrderProducts.Pages,
                PageCount: this.PreOrderProducts.PageCount
              }
            },
            PaginationConfig() {
              return {
                Page: this.Page
              }
            },
            customerCurrencySymbol() { 
                return this.$store.getters.userInfo.customerCurrencySymbol;
            }
        },
        created() {
          this.loadingProducts = true;

          // Load preorder data
          this.$store.dispatch('getPreOrderById', this.$attrs.id).then(() => {
            this.disableWatch = false
          });
          // Load totals
          this.$store.dispatch('getPreOrderTotalsById', this.$attrs.id);

          // Load preorder products with a limit
          this.$store.dispatch('getPreOrderProductsById', {
            id: this.$attrs.id,
            body: {
              PageSize: 10
            }
          }).then(() => {
            this.disableWatch = false
            this.loadingProducts = false
            this.loaded = { loaded: true }
          });

          // Load marketing products
          this.$store.dispatch('getPreOrderMarketingProductsById', {
            id: this.$attrs.id,
            body: {
              PageSize: 50
            }
          }).then(() => { 
              this.disableWatch = false 
          });
        },
        watch: {
            "PreOrder.ProductGroups"() {
                this.ctaButtonDisabled = false;
            },
            "PreOrder.Address"() {
                if (!this.disableWatch) {
                    let disableFetch = true;
                    this.savePreOrderData(disableFetch);
                }
            },
            "PreOrder.CustomerComment"() {
                if (!this.disableWatch) {
                    let disableFetch = true;
                    this.savePreOrderData(disableFetch);
                }
            },
            "PreOrder.EarlyShipmentAllowed"() {
                if (!this.disableWatch) {
                    this.savePreOrderData();
                }
            }
        },
        methods: {
            savePreOrderData: debounce(function (disableFetch = false) {
                this.$store.dispatch('updatePreOrderCustomer', {
                    PreOrderId: this.PreOrder.Id,
                    body: {
                        Id: this.PreOrder.Id,
                        Address: this.PreOrder.Address,
                        OrderReference: this.PreOrder.OrderReference,
                        PreOrderDiscount: this.PreOrder.PreOrderDiscount,
                        CustomerComment: this.PreOrder.CustomerComment,
                        EarlyShipmentAllowed: this.PreOrder.EarlyShipmentAllowed,
                        InternalCustomerReference: this.PreOrder.InternalCustomerReference
                    }
                }).then(() => {
                    if (!disableFetch) {
                        this.fetchPreOrder();
                    }
                });
            }, 500),
            fetchPreOrder() {
                this.disableWatch = true;
                this.$store.dispatch('getPreOrderById', this.$attrs.id).then(() => {
                    this.disableWatch = false
                });
            },
            fetchPreOrderProducts() {
              this.loadingProducts = true;
              this.loaded = {};

              this.$store.dispatch('getPreOrderProductsById', {
                id: this.$attrs.id,
                body: {
                  Page: this.Page,
                  PageSize: 10,
                  Search: this.searchTerm,
                  AmountsOnly: this.amountsOnly
                }
              }).then(() => {
                this.loadingProducts = false;
                this.loaded = { loaded: true }
              }).catch(() => {
                this.loadingProducts = false;
                this.loaded = { loaded: true }
              })
            },
            searchPreOrderProducts() {
              this.Page = 1;
              this.fetchPreOrderProducts();
            },
            fetchPaginated(config) {
              this.Page = config.Page;
              this.fetchPreOrderProducts();
            },
            notifySaved() {
                this.$notify({
                    group: 'global',
                    title: this.$t('global.saved'),
                    type: 'success',
                    duration: 2000
                });
            },
            sendPreOrder() {
                const confirm = window.confirm('Weet u zeker dat u deze pre-order naar de vertegenwoordiger wilt verzenden?')
                if (confirm) {
                    this.$store.dispatch('sendToSales', this.PreOrder.Id).then(() => {
                        this.$notify({
                            group: 'global',
                            title: this.$t('global.sent_to_salesman', { 'preorder': this.PreOrder.Brand + ' - ' + this.PreOrder.Collection}),
                            type: 'success',
                            duration: 3000
                        });
                        this.ctaButtonDisabled = true;
                    });
                }
            },
            onConcurrencyException() {
                this.pageDisabled = true;
                this.$notify({
                    group: 'global',
                    title: this.$t('concurrency.pre_order'),
                    type: 'info',
                    duration: 5000
                });

                setTimeout(function() {
                    location.reload(true);
                }, 4000);
            },
            updateUnread(value) {
                this.$set(this.PreOrder, 'UnreadMessages', value)
            },
            sizeChartCopied(sizeChartCopy) {
                this.sizeChartCopy = sizeChartCopy;
            }
        },
        beforeDestroy() {
            this.$store.dispatch('resetPreOrder');
        }
    }
</script>

<style lang="scss">
    @import "~@/assets/scss/private";

    .row__delivery-address {
        vertical-align: top;
    }

    @include down($tablet) {
        table.u-fullwidth {
            td:first-child {
                width: 50%;
            }
        }
    }
</style>



// WEBPACK FOOTER //
// src/views/customer/preOrders/Edit.vue